import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TestTube, Upload, Trash2, Download, FileText, User, Search } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMedicalTests = () => {
  const { t } = useLanguage();
  const [patients, setPatients] = useState([]);
  const [allTests, setAllTests] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [testName, setTestName] = useState('');
  const [description, setDescription] = useState('');
  const [testDate, setTestDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');

  const getToken = () => localStorage.getItem('adminToken');

  useEffect(() => {
    fetchPatients();
    fetchAllTests();
  }, []);

  const fetchPatients = async () => {
    try {
      const res = await axios.get(`${API}/admin/patients`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setPatients(res.data);
    } catch (e) { toast.error('Failed to load patients'); }
  };

  const fetchAllTests = async () => {
    try {
      const res = await axios.get(`${API}/admin/all-medical-tests`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setAllTests(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedPatient) { toast.error('Please select a patient first'); return; }
    if (!testName.trim()) { toast.error('Please enter a test name'); e.target.value = ''; return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('patient_id', selectedPatient);
      formData.append('test_name', testName);
      formData.append('description', description);
      formData.append('test_date', testDate);
      await axios.post(`${API}/medical-tests/upload`, formData, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' } });
      toast.success('Test uploaded!');
      setTestName(''); setDescription('');
      fetchAllTests();
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this test?')) return;
    try {
      await axios.delete(`${API}/medical-tests/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Deleted');
      setAllTests(allTests.filter(t => t.id !== id));
    } catch (e) { toast.error('Failed'); }
  };

  const filtered = allTests.filter(t => {
    const matchesSearch = !searchTerm ||
      (t.patient_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.test_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.filename || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-medical-tests-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{t.patientMedicalTests}</h1>
        <p className="text-[#6B7280]">{t.patientMedicalTestsSubtitle}</p>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
        <h2 className="text-lg font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t.uploadNewTest}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <select value={selectedPatient} onChange={e => setSelectedPatient(e.target.value)} className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="upload-patient-select">
            <option value="">Select Patient *</option>
            {patients.map(p => <option key={p.id} value={p.id}>{p.name} - {p.email}</option>)}
          </select>
          <input type="text" value={testName} onChange={e => setTestName(e.target.value)} placeholder="Test name *" className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20" data-testid="admin-test-name" />
          <input type="date" value={testDate} onChange={e => setTestDate(e.target.value)} className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)" className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20" />
          <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[#0F4C5C]/30 rounded-lg cursor-pointer hover:bg-[#0F4C5C]/5 transition-colors" data-testid="test-upload-btn">
            <Upload className="w-5 h-5 text-[#0F4C5C]" />
            <span className="text-sm font-medium text-[#0F4C5C]">{uploading ? t.uploading : t.uploadPdfImage}</span>
            <input type="file" accept="image/*,.pdf" onChange={handleUpload} className="hidden" disabled={uploading} />
          </label>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
          <input type="text" placeholder={t.searchTests} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="test-search" />
        </div>
      </div>

      {/* Tests List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <TestTube className="w-12 h-12 text-[#6B7280] mx-auto mb-4" />
          <p className="text-[#6B7280]">{searchTerm ? t.noResults : t.noTestsUploadedYet}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(test => (
            <div key={test.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 flex items-center justify-between" data-testid={`test-card-${test.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#0F4C5C]/10 rounded-lg flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-[#0F4C5C]" /></div>
                <div>
                  <div className="font-semibold text-[#1F2937]">{test.test_name}</div>
                  <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{test.patient_name}</span>
                    <span>|</span>
                    <span>{new Date(test.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                    {test.description && <><span>|</span><span>{test.description}</span></>}
                  </div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{test.filename} | {t.uploadedBy} {test.uploaded_by}</div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <a href={`${API}/medical-tests/file/${test.id}?token=${getToken()}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1.5 bg-[#0F4C5C] text-white rounded-lg text-sm hover:bg-[#0F4C5C]/90 transition-colors" data-testid={`view-test-${test.id}`}>
                  <Download className="w-3.5 h-3.5" /> {t.view}
                </a>
                <button onClick={() => handleDelete(test.id)} className="px-3 py-1.5 text-red-500 border border-red-200 rounded-lg text-sm hover:bg-red-50 transition-colors" data-testid={`delete-test-${test.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMedicalTests;
